import type { User } from '@/modules/users/interfaces/user'

/**
 * Espejo de `ASSIGNABLE` en `app/Support/RoleHierarchy.php` (repo BookingV2):
 * qué roles puede ASIGNAR cada rol — su nivel para abajo.
 *
 * LAS DOS COPIAS SE MUEVEN JUNTAS. Son repos distintos y nada las ata en tiempo
 * de compilación, así que del lado de BookingV2 hay un test de contrato
 * (`tests/Unit/RoleHierarchyMirrorTest.php`) que fija la tabla y truena nombrando
 * este archivo si alguien la cambia de un solo lado. Aquí esta tabla solo decide
 * qué se pinta deshabilitado; el candado de verdad vive en el backend.
 */
export const ASSIGNABLE_ROLES: Record<string, string[]> = {
	Master: ['Master', 'Travel Manager', 'Supervisor', 'Traveler'],
	'Travel Manager': ['Travel Manager', 'Supervisor', 'Traveler'],
	Supervisor: ['Supervisor', 'Traveler'],
	Traveler: [],
}

/**
 * Orden de rango DERIVADO de `ASSIGNABLE_ROLES`: quien puede asignar más roles
 * está más arriba. Da Master 0 · Travel Manager 1 · Supervisor 2 · Traveler 3.
 *
 * Se deriva a propósito en vez de escribir la tabla numérica otra vez. Antes esa
 * tabla estaba copiada a mano en tres componentes y no la vigilaba nada: el test
 * espejo de BookingV2 cazaba una deriva de `ASSIGNABLE_ROLES` y se quedaba callado
 * sobre las copias, así que la pantalla podía acabar contradiciéndose (sección
 * editable pero el combobox sin ofrecer el rol, o al revés). Derivándolo, ese
 * mismo test cubre las dos decisiones. Sigue habiendo un paso humano —el test vive
 * del lado PHP y sólo NOMBRA este archivo al tronar—, pero ya no hay forma de
 * mover una mitad del front y olvidar la otra.
 *
 * El orden se apoya en que los largos sean DISTINTOS: hoy son 4·3·2·0 y no hay
 * empate posible. Si algún día dos roles pueden asignar la misma cantidad, el
 * desempate queda a merced del orden de llaves y NO está definido: en ese momento
 * hay que escribir el orden explícito aquí en vez de derivarlo.
 */
const RANK_ORDER: string[] = Object.keys(ASSIGNABLE_ROLES).sort(
	(a, b) => ASSIGNABLE_ROLES[b].length - ASSIGNABLE_ROLES[a].length
)

/** Rango del rol: 0 es el más alto. `-1` si no es un rol cliente. */
export const roleRank = (role: string): number => RANK_ORDER.indexOf(role)

/**
 * Rango del actor = el más alto de sus roles cliente. `null` si no tiene ninguno
 * (p. ej. staff interno), que cada pantalla resuelve distinto: unas ofrecen todo
 * y otras nada, así que no se decide aquí.
 */
export const actorRank = (actor: User | undefined): number | null => {
	const ranks = (actor?.roles ?? [])
		.map((role) => roleRank(role))
		.filter((rank) => rank >= 0)

	return ranks.length ? Math.min(...ranks) : null
}

/** Espejo de `RoleHierarchy::canAssign`. Interno (staff) puede con todo. */
export const canAssignRole = (
	actor: User | undefined,
	role: string
): boolean => {
	if (!actor) return false
	if (actor.internal) return true

	return (actor.roles ?? []).some((actorRole) =>
		(ASSIGNABLE_ROLES[actorRole] ?? []).includes(role)
	)
}

/**
 * Espejo de `RoleHierarchy::canManageRolesOf`: TODOS los roles del objetivo
 * deben ser asignables por el actor. Un objetivo sin roles lo maneja cualquiera
 * (igual que el `every()` sobre colección vacía del backend).
 */
export const canManageRolesOf = (
	actor: User | undefined,
	target: User | undefined
): boolean => {
	if (!actor || !target) return false
	if (actor.internal) return true

	return (target.roles ?? []).every((role) => canAssignRole(actor, role))
}

/**
 * Espejo del candado de CREDENCIALES de `UpdateUserRequest`: el correo de login
 * (`email`) y el acceso al sistema (`have_access`) solo se pueden cambiar sobre
 * uno mismo o sobre un usuario que NO supera en rango al actor. Un Travel
 * Manager administra el PERFIL de un Master pero no sus credenciales — cambiarle
 * el correo sería un account-takeover (swap de correo → forgot-password →
 * set-password → entrar como él).
 */
export const canManageCredentialsOf = (
	actor: User | undefined,
	target: User | undefined
): boolean => {
	if (!actor || !target) return false

	return actor.id === target.id || canManageRolesOf(actor, target)
}
